import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Wand2 } from "lucide-react";
import { api, IMAGES } from "../lib/api";
import ProductCard from "../components/ProductCard";
import StickerBadges, { Sparkle } from "../components/StickerBadges";

function Hero() {
  return (
    <section className="grain relative overflow-hidden" data-testid="home-hero">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:pt-20">
        <div className="rise relative">
          <Sparkle className="-left-2 -top-6 h-6 w-6" />
          <p className="font-script text-2xl text-[var(--blue-soft)]">Sweet Treats made with love</p>
          <h1 className="mt-3 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">
            <span className="text-[var(--pink)]">Dipped,</span> boxed &amp;{" "}
            <span className="text-[var(--blue-soft)]">bow-tied</span> for every celebration.
          </h1>
          <p className="mt-6 max-w-lg text-base text-white/75 sm:text-lg">
            Chocolate-dipped strawberries, letter gift boxes and bold pink &amp; royal blue cake mixes from L3's Kitchen Co. and More.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/shop" className="btn-candy" data-testid="hero-shop-button">Shop Treats <ArrowRight className="h-5 w-5" /></Link>
            <Link to="/custom-order" className="btn-royal" data-testid="hero-custom-order-button"><Wand2 className="h-5 w-5" /> Request a Custom Order</Link>
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-lg">
          <Sparkle className="-right-2 top-4 h-8 w-8" color="var(--blue-soft)" delay={0.6} />
          <Sparkle className="-left-4 bottom-16 h-5 w-5" delay={1.2} />
          <div className="rise overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_0_0_6px_var(--pink),0_0_0_12px_var(--blue)]" style={{ animationDelay: "150ms" }}>
            <img src={IMAGES.ring} alt="Dipped strawberry ring gift box" className="aspect-[4/3.6] w-full object-cover" />
          </div>
          <div className="wobble absolute -bottom-8 -left-4 w-40 overflow-hidden rounded-2xl border-4 border-white shadow-2xl sm:w-48">
            <img src={IMAGES.letter} alt="Letter strawberry box" className="aspect-square w-full object-cover" />
          </div>
          <span className="sticker absolute -right-3 -top-5 flex h-24 w-24 rotate-12 flex-col items-center justify-center text-center" data-testid="hero-sticker">
            <Heart className="h-5 w-5 fill-[var(--pink)] text-[var(--pink)]" />
            <span className="font-display text-sm leading-tight">Gift<br />Ready</span>
          </span>
        </div>
      </div>
    </section>
  );
}

function Featured() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data)); }, []);
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="featured-section">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-script text-xl text-[var(--pink)]">Fan favorites</p>
          <h2 className="text-4xl sm:text-5xl">Featured Treats</h2>
        </div>
        <Link to="/shop" className="font-extrabold text-[var(--blue-soft)] hover:text-[var(--pink)]" data-testid="featured-view-all-link">View all treats →</Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
        {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </section>
  );
}

function CakeMixBanner() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6" data-testid="cake-mix-banner">
      <div className="panel grid overflow-hidden lg:grid-cols-2">
        <img src={IMAGES.mix} alt="Pink and Royal Blue cake mixes" className="h-full max-h-[460px] w-full object-cover" />
        <div className="flex flex-col justify-center gap-5 p-8 sm:p-12">
          <p className="font-script text-2xl text-[var(--blue-soft)]">Color your cake with L3's!</p>
          <h2 className="text-4xl sm:text-5xl"><span className="text-[var(--pink)]">Pink</span> &amp; <span className="text-[var(--blue-soft)]">Royal Blue</span> Cake Mix</h2>
          <p className="text-white/70">Same great taste, just a different color. Moist, delicious and perfect for cakes, cupcakes, gender reveals and more.</p>
          <Link to="/shop?category=Cake%20Mixes" className="btn-candy self-start" data-testid="cake-mix-shop-button">Shop Cake Mixes</Link>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="about-section">
      <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:items-center">
        <div>
          <p className="font-script text-xl text-[var(--pink)]">About L3's</p>
          <h2 className="text-4xl sm:text-5xl">A small kitchen with a big heart.</h2>
          <p className="mt-5 text-white/70">Every box is hand-dipped, hand-arranged and tied with our pink &amp; royal blue ribbon. Birthdays, Valentine's, gender reveals or just because, we make it sweet.</p>
          <div className="mt-8 inline-block rounded-2xl border-2 border-[var(--pink)] p-1">
            <div className="rounded-xl border-2 border-[var(--blue)] bg-white px-6 py-4 text-center text-black">
              <p className="font-script text-3xl text-[var(--pink)]">Thank You</p>
              <p className="text-sm font-bold">for supporting my small business!</p>
            </div>
          </div>
        </div>
        <StickerBadges />
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <div className="ribbon" />
      <Featured />
      <CakeMixBanner />
      <About />
      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-[2rem] bg-[var(--pink)] p-10 sm:p-14" data-testid="custom-cta">
          <Sparkle className="right-10 top-8 h-10 w-10" color="#fff" />
          <h2 className="text-4xl text-white sm:text-5xl">Dreaming up something special?</h2>
          <p className="mt-3 max-w-xl text-white/90">Tell us your colors, theme and date. We'll design treats that match your celebration perfectly.</p>
          <Link to="/custom-order" className="btn-royal mt-8" data-testid="cta-custom-order-button">Request a Custom Order</Link>
        </div>
      </section>
    </div>
  );
}
